/**
 * Online Coaching – list approved coaches, filter by specialization, search by name.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { listApprovedCoaches, getMyCoachId, getCoach } from '../../domain/usecases/coaching';
import Card from '../../shared/ui/Card.jsx';
import Avatar from '../../shared/ui/Avatar.jsx';
import Badge from '../../shared/ui/Badge.jsx';
import Button from '../../shared/ui/Button.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import Display from '../../shared/ui/Display.jsx';
import Title from '../../shared/ui/Title.jsx';
import Body from '../../shared/ui/Body.jsx';
import Select from '../../shared/ui/Select.jsx';
import Input from '../../shared/ui/Input.jsx';
import styles from './CoachingPage.module.css';

const SPECIALIZATION_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'powerlifting', label: 'Powerlifting' },
  { value: 'bodybuilding', label: 'Bodybuilding' },
  { value: 'strongman', label: 'Strongman' },
];

export function CoachingPage() {
  const { userId } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specialization, setSpecialization] = useState('');
  const [searchName, setSearchName] = useState('');
  const [myCoach, setMyCoach] = useState(null); // coach doc or null

  // Fetch the user's active coach (if any).
  useEffect(() => {
    if (!userId) return;
    getMyCoachId(userId).then((coachId) => {
      if (!coachId) return;
      getCoach(coachId).then((coachDoc) => setMyCoach(coachDoc || null));
    });
  }, [userId]);

  useEffect(() => {
    setLoading(true);
    listApprovedCoaches(100, specialization || null)
      .then(setCoaches)
      .finally(() => setLoading(false));
  }, [specialization]);

  const filtered = searchName.trim()
    ? coaches.filter((c) => c.name.toLowerCase().includes(searchName.trim().toLowerCase()))
    : coaches;

  return (
    <div className={styles.page}>
      <Display as="h1" size="l">Online Coaching</Display>
      <Body tone="secondary" style={{ marginBottom: '0.5rem' }}>
        Find a coach and request coaching.
      </Body>

      {myCoach && (
        <Card frame="gold" style={{ marginBottom: '1.25rem' }}>
          <Title as="h2" style={{ marginBottom: '0.5rem' }}>Your coach</Title>
          <div className={styles.cardInner} style={{ marginBottom: '1rem' }}>
            <Avatar
              src={myCoach.profileImage}
              initials={(myCoach.name || 'C').charAt(0)}
              size="md"
              verified={myCoach.isVerified}
              alt={myCoach.name}
            />
            <div className={styles.info}>
              <Title as="span" style={{ fontSize: 17, display: 'block', marginBottom: 2 }}>
                {myCoach.name}
              </Title>
              {myCoach.specialization && (
                <Badge variant="gold" style={{ textTransform: 'capitalize' }}>
                  {myCoach.specialization}
                </Badge>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Button variant="primary" as={Link} to="/coaching/my-coach">
              View my coach
            </Button>
            <Button variant="secondary" as={Link} to="/coaching/my-plan">
              View my plan
            </Button>
            <Button variant="secondary" as={Link} to="/coaching/my-programs">
              View my programs
            </Button>
          </div>
        </Card>
      )}

      {!myCoach && (
        <div className={styles.links}>
          <Link to="/coaching/my-plan" className={styles.navLink}>My Coaching Plan →</Link>
          <Link to="/coaching/my-programs" className={styles.navLink}>My programs →</Link>
          <Button variant="ghost" size="sm" as={Link} to="/coaching/requests">
            My requests →
          </Button>
        </div>
      )}

      {myCoach && (
        <div style={{ marginBottom: '0.75rem' }}>
          <Button variant="ghost" size="sm" as={Link} to="/coaching/requests">
            My requests →
          </Button>
        </div>
      )}

      <div className={styles.filters}>
        <Select
          value={specialization}
          onChange={(e) => setSpecialization(e.target.value)}
          options={SPECIALIZATION_OPTIONS}
        />
        <Input
          type="search"
          placeholder="Search by name"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {loading ? (
        <div className={styles.skeletonList}>
          {[1, 2, 3].map((i) => <Skeleton key={i} width="100%" height={80} radius="var(--radius-md)" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="user"
          title="No coaches found"
          body="Try a different filter or check back later."
        />
      ) : (
        <ul className={styles.list}>
          {filtered.map((c) => (
            <li key={c.id}>
              <Card className={styles.card}>
                <div className={styles.cardInner}>
                  <Avatar
                    src={c.profileImage}
                    initials={c.name.charAt(0)}
                    size="md"
                    verified={c.isVerified}
                    alt={c.name}
                  />
                  <div className={styles.info}>
                    <Title as="span" style={{ fontSize: 17, lineHeight: '22px', display: 'block', marginBottom: 2 }}>
                      {c.name}
                    </Title>
                    {c.specialization && (
                      <Badge variant="gold" style={{ marginBottom: 4, textTransform: 'capitalize' }}>
                        {c.specialization}
                      </Badge>
                    )}
                    {c.bio && (
                      <Body tone="secondary" as="p" style={{ fontSize: 13, lineHeight: '18px', margin: 0 }}>
                        {c.bio.slice(0, 80)}{c.bio.length > 80 ? '…' : ''}
                      </Body>
                    )}
                  </div>
                  <Button variant="secondary" size="sm" as={Link} to={`/coaching/${c.id}`}>
                    View
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
